import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, SimulationCompleteError } from '@/lib/apiClient';

/**
 * Shape of `GET /api/{module}/{entity}/{id}/available-actions`. Declared here rather
 * than imported from a generated hook: this is the API contract, and the generated
 * files are the generator's to rewrite.
 */
export interface ActionMetadata {
  id: string;
  name: string;
  workflowName: string;
  stepName: string;
  permission: string;
  enabled: boolean;
  reason?: string;
  missingFields?: string[];
}

export interface WorkflowActions {
  workflowName: string;
  actions: ActionMetadata[];
}

export interface CommandAction {
  id: string;
  name: string;
  displayName: string;
  permission: string;
  /**
   * The state machine the command drives, named by the entity field the machine binds
   * ("status", "paymentStatus"); '' for a command declared with skipStateMachine. An entity may
   * carry several machines, and the generated actions menu groups commands on this.
   */
  stateMachine: string;
  enabled: boolean;
  reason?: string;
  missingFields?: string[];
}

export interface AvailableActionsResponse {
  workflows: WorkflowActions[];
  commands?: CommandAction[];
}

export interface TriggerWorkflowInput {
  workflowName: string;
  stepName: string;
}

export interface ExecuteCommandInput {
  commandName: string;
  input?: Record<string, unknown>;
}

/**
 * Entity-agnostic twin of the 21 generated `use{Entity}Actions` hooks.
 *
 * Those hooks are byte-identical apart from two lowercased names in a URL, but a
 * React hook cannot be picked by name at runtime — so anything that only learns
 * which entity it is dealing with while rendering (the command palette) needs this.
 * It deliberately does not replace the generated hooks: those are what the detail
 * views use, and they stay the generator's business.
 *
 * The query key mirrors the generated one exactly — `[entity, id, 'available-actions']`
 * with the entity lowercased. That is load-bearing, not cosmetic: a detail view has
 * already fetched this for its actions menu, so matching the key turns the palette's
 * read into a cache hit instead of a second round trip on every Cmd+K.
 */

export interface EntityActionsTarget {
  /** Module name as it appears in the generated navigation, e.g. 'Sales'. */
  module: string | undefined;
  /** Entity name as it appears in the generated navigation, e.g. 'Order'. */
  entity: string | undefined;
  entityId: string | undefined;
}

export function availableActionsQueryKey(entity: string, entityId: string | undefined) {
  return [entity.toLowerCase(), entityId, 'available-actions'] as const;
}

// Stable identities: `data?.x ?? []` would hand consumers a fresh array on every
// render, defeating any useMemo keyed on them.
const NO_WORKFLOWS: WorkflowActions[] = [];
const NO_COMMANDS: CommandAction[] = [];

export function useEntityActions({ module, entity, entityId }: EntityActionsTarget) {
  const queryClient = useQueryClient();
  /**
   * The generator is not consistent about the entity segment: 15 of the 21 hooks
   * lowercase it whole (`/api/finance/planheader/`) while 6 keep camelCase
   * (`/api/core/enumValue/`, `/api/inventory/inventoryReservation/`). Both forms
   * ship and both work, because ASP.NET Core matches routes case-insensitively —
   * which is also why the inconsistency has gone unnoticed. Lowercasing is therefore
   * safe here, but do not read the generated hooks as defining a convention.
   */
  const basePath = `/api/${module?.toLowerCase()}/${entity?.toLowerCase()}/${entityId}`;

  const { data, isLoading } = useQuery<AvailableActionsResponse>({
    queryKey: availableActionsQueryKey(entity ?? '', entityId),
    queryFn: async () => {
      const response = await apiClient.get<AvailableActionsResponse>(`${basePath}/available-actions`);
      return response.data;
    },
    enabled: !!module && !!entity && !!entityId && entityId !== 'new',
  });

  /**
   * The generated hooks invalidate a plural root (`['orders']`) alongside the
   * singular one, but not identically — Product uses `['product', id]` where Order
   * uses `['orders']`. Since this hook cannot know which convention a given entity
   * follows, it invalidates both roots. Over-invalidating costs a refetch; under-
   * invalidating leaves the detail view showing state the action just changed.
   */
  const invalidate = () => {
    const key = (entity ?? '').toLowerCase();
    queryClient.invalidateQueries({ queryKey: [key] });
    queryClient.invalidateQueries({ queryKey: [`${key}s`] });
  };

  const triggerWorkflow = useMutation({
    mutationFn: async (input: TriggerWorkflowInput) => {
      const response = await apiClient.post(`${basePath}/trigger-workflow`, input);
      return response.data;
    },
    onSuccess: invalidate,
    onError: (error: unknown) => {
      // A completed simulation is a normal outcome, not a failure: the simulation
      // modal picks it up. Matches the generated hooks.
      if (error instanceof SimulationCompleteError) return;
    },
  });

  const executeCommand = useMutation({
    mutationFn: async (input: ExecuteCommandInput) => {
      const response = await apiClient.post(
        `${basePath}/${input.commandName.toLowerCase()}`,
        input.input || {},
      );
      return response.data;
    },
    onSuccess: invalidate,
  });

  return {
    workflows: data?.workflows ?? NO_WORKFLOWS,
    commands: data?.commands ?? NO_COMMANDS,
    isLoading,
    triggerWorkflow,
    executeCommand,
  };
}
